<template>
<span>
    <block type="combobox"
           :id="'month.' + id"
           :close-title="true"
           v-model="monthInput"
           :state-id="stateId"
           :focused="monthFocused"
           @focus="() => {$emit('focus'); monthFocused=true;}"
           @blur="() => {$emit('blur'); monthFocused=false;}"
           @finished="() => {hideDay ? (yearFocused = true) : (dayFocused = true)}"
           :data-qa="'month.' + id"
           :values="months"
           :parent-take-width="parentTakeWidth"
           :as-field="true"
           :step-fields-count="stepFieldsCount"
           @invalid="value => monthInternalInvalid = value"
           @valid="() => monthInternalInvalid = null"
           :settings="monthSettings"></block>

    <block v-if="!hideDay"
           type="combobox"
           :id="'day.' + id"
           :close-title="true"
           :as-field="true"
           :step-fields-count="stepFieldsCount"
           v-model="dayInput"
           :state-id="stateId"
           :values="days"
           :parent-take-width="parentTakeWidth"
           :focused="dayFocused"
           :data-qa="'day.' + id"
           @focus="() => {$emit('focus'); dayFocused=true;}"
           @blur="() => {$emit('blur'); dayFocused=false;}"
           @finished="() => {yearFocused = true}"
           @invalid="value => dayInternalInvalid = value"
           @valid="() => dayInternalInvalid = null"
           :settings="daySettings"/>

    <block type="combobox"
           :id="'year.' + id"
           v-model="yearInput"
           :close-title="true"
           :data-qa="'year.' + id"
           :as-field="true"
           :step-fields-count="stepFieldsCount"
           :values="years"
           :parent-take-width="parentTakeWidth"
           :state-id="stateId"
           :focused="yearFocused"
           @focus="() => {$emit('focus'); yearFocused=true;}"
           @blur="() => {$emit('blur'); yearFocused=false;}"
           @finished="$emit('finished');"
           @invalid="value => yearInternalInvalid = value"
           @valid="() => yearInternalInvalid = null"
           :settings="yearSettings"/>
</span>
</template>

<script>
import '@/scss/blocks/block.scss';
import '@/scss/blocks/datesimple.scss';
import '@/scss/blocks/error-message.scss';
import { date2str, str2date, daysInMonth, DATE_FORMAT } from '@/utils/format';

export default {
    components: {
        block: require('@/vue/block').default,
    },
    mixins: [
        require('@/utils/mixins/validation').default,
        require('@/utils/mixins/focus').default,
    ],
    props: {
        stepFieldsCount: {},
        id: { type: String, required: true },
        adaptive: { type: Boolean, default: false },
        required: { type: Boolean, default: false },
        format: { type: String, default: DATE_FORMAT },
        dataQa: { type: String, default: '' },
        value: { type: String, default: '' },
        dateFrom: { type: String, default: '' },
        dateTo: { type: String, default: '' },
        defaultDay: { type: String, default: '' },
        defaultMonth: { type: String, default: '' },
        defaultYear: { type: String, default: '' },
        hideDay: { type: Boolean, default: false },
        monthAutocomplete: { type: String },
        dayAutocomplete: { type: String },
        yearAutocomplete: { type: String },
        showNotMatched: { type: Boolean, default: false },
    },
    data () {
        let date = this.value && str2date(this.value, this.format);
        if (!date && (this.defaultDay || this.hideDay) && this.defaultMonth && this.defaultYear) {
            date = new Date(this.defaultYear, this.defaultMonth - 1, this.hideDay ? 1 : this.defaultDay);
            this.$emit('input', date2str(date, this.format));
        }

        return {
            yearInput: date ? String(date.getFullYear()) : this.defaultYear || null,
            monthInput: date ? String(date.getMonth()) : (this.defaultMonth ? String(this.defaultMonth - 1) : null),
            dayInput: date ? String(date.getDate()) : (this.defaultDay ? String(Number(this.defaultDay)) : (this.hideDay ? '1' : null)),

            monthFocused: false,
            yearFocused: false,
            dayFocused: false,

            monthInternalInvalid: null,
            yearInternalInvalid: null,
            dayInternalInvalid: null,
        };
    },
    computed: {
        parentTakeWidth () {
            if (this.hideDay) {
                return 9;
            }
            return 12;
        },
        monthSettings () {
            return {
                error: (this.validation_showError && this.validation_error) ? this.validation_error : null,
                showNotMatched: this.showNotMatched,
                autoselectOptions: true,
                autocomplete: this.monthAutocomplete,
                required: this.required,
                validation_internal: true,
                optionsSize: true,
                placeholder: this.$trl('aw.month'),
                invalid: this.dateInvalid && this.validation_showError,
                adaptive: this.adaptive,
                innerWrap: true,
                takeWidth: 6,
            };
        },
        daySettings () {
            return {
                invalid: this.dateInvalid && this.validation_showError,
                keyboard: 'number',
                showNotMatched: this.showNotMatched,
                required: this.required,
                validation_internal: true,
                optionsSize: true,
                autocomplete: this.dayAutocomplete,
                placeholder: this.$trl('aw.day'),
                adaptive: this.adaptive,
                innerWrap: true,
                takeWidth: 3,
            };
        },
        yearSettings () {
            return {
                invalid: this.dateInvalid && this.validation_showError,
                keyboard: 'number',
                showNotMatched: this.showNotMatched,
                required: this.required,
                validation_internal: true,
                optionsSize: true,
                autocomplete: this.yearAutocomplete,
                placeholder: this.$trl('aw.year'),
                adaptive: this.adaptive,
                innerWrap: true,
                takeWidth: 3,
            };
        },
        months () {
            let months = Array(12).fill(true)
                .map((_, index) => {
                    let title = date2str(new Date(1970, index, 1), '#month');
                    return {
                        type: 'option',
                        match: `${index < 9 ? '0' : ''}${index + 1} ${index + 1} ${title}`,
                        title,
                        id: String(index),
                    };
                });

            // if (this.yearInput &&
            //     this.dateFromDate &&
            //     this.dateFromDate.getFullYear() === Number(this.yearInput)
            // ) {
            //     let fromMonth = this.dateFromDate.getMonth();
            //     return months.filter(month => Number(month.id) >= fromMonth);
            // }

            // if (this.yearInput &&
            //     this.dateToDate &&
            //     this.dateToDate.getFullYear() === Number(this.yearInput)
            // ) {
            //     let toMonth = this.dateToDate.getMonth();
            //     return months.filter(month => Number(month.id) <= toMonth);
            // }

            return months;
        },
        days () {
            let daysCount = 31;
            if (this.monthInput && this.yearInput) {
                daysCount = daysInMonth(this.monthInput, this.yearInput);
            }
            let days = Array(daysCount).fill(true)
                .map((_, index) => {
                    let day = String(index + 1);
                    return {
                        type: 'option',
                        title: day,
                        id: day,
                        match: day.length === 1 ? day + ' 0' + day : day,
                    };
                });

            // if (this.yearInput &&
            //     this.monthInput &&
            //     this.dateFromDate &&
            //     this.dateFromDate.getFullYear() === Number(this.yearInput) &&
            //     this.dateFromDate.getMonth() === Number(this.monthInput)
            // ) {
            //     let fromDay = this.dateFromDate.getDate();
            //     return days.filter(days => Number(days.id) >= fromDay);
            // }

            // if (this.yearInput &&
            //     this.monthInput &&
            //     this.dateToDate &&
            //     this.dateToDate.getFullYear() === Number(this.yearInput) &&
            //     this.dateToDate.getMonth() === Number(this.monthInput)
            // ) {
            //     let toDay = this.dateToDate.getDate();
            //     return days.filter(days => Number(days.id) <= toDay);
            // }

            return days;
        },
        years () {
            let yearsRange = this.dateToDate.getFullYear() - this.dateFromDate.getFullYear();
            let years = Array(Number(yearsRange) + 1).fill(true)
                .map((_, index) => {
                    let year = String(this.dateToDate.getFullYear() - yearsRange + index);
                    return {
                        type: 'option',
                        title: year,
                        id: year,
                        match: `${year} ${year.slice(2)}`,
                    };
                });

            return this.dateToDate > new Date() ? years : years.reverse();
        },
        dateInput () {
            if (this.yearInput && this.monthInput && this.dayInput) {
                return new Date(this.yearInput, this.monthInput, this.dayInput);
            }

            return null;
        },
        dateFromDate () {
            return this.dateFrom ? str2date(this.dateFrom, this.format) : new Date(new Date(1, 0, 1).setFullYear(1900));
        },
        dateToDate () {
            if (!this.dateFrom && !this.dateTo) {
                return new Date();
            }

            if (this.dateTo) {
                return new Date(this.dateTo);
            }

            return new Date(new Date(1, 0, 1).setFullYear(2100));
        },
        partiallyFilled () {
            if (this.hideDay) {
                return Boolean(this.yearInput || this.monthInput) &&
                        Boolean(!this.yearInput || !this.monthInput) &&
                        this.$trl('aw.partially_filled_invalid');
            }

            return Boolean(this.dayInput || this.yearInput || this.monthInput) &&
                   Boolean(!this.dayInput || !this.yearInput || !this.monthInput) &&
                   this.$trl('aw.partially_filled_invalid');
        },
        dateInvalid () {
            if (this.dayInput === null) {
                return false;
            }
            let nonExistingDay = !this.days.find(day => this.dayInput === day.id);

            return nonExistingDay && this.$trl('aw.date_non_existing');
        },
        dateInvalidTo () {
            return !this.dayInput || !this.yearInput || !this.monthInput
                ? undefined
                : (this.dateToDate < new Date(this.yearInput, this.monthInput, this.dayInput) && this.$trl('aw.date_to_invalid'));
        },
        dateInvalidFrom () {
            return !this.dayInput || !this.yearInput || !this.monthInput
                ? undefined
                : (this.dateFromDate > new Date(this.yearInput, this.monthInput, this.dayInput) && this.$trl('aw.date_from_invalid'));
        },
        dateString () {
            return (!this.dateInput && this.validation_error) ? undefined : date2str(new Date(this.yearInput, this.monthInput, this.dayInput), this.format);
        },
    },
    watch: {
        dateString (value) {
            this.$emit('input', value);
        },
        monthInput () { this.validation_changed(); },
        dayInput () { this.validation_changed(); },
        yearInput () { this.validation_changed(); },
    },
    methods: {
        focus_focus () {
            if (!this.monthFocused && !this.yearFocused && !this.dayFocused) {
                this.monthFocused = true;
            }
        },
        validation_focusError () {
            if (this.monthInternalInvalid || !this.monthInput) {
                this.monthFocused = true;
            } else if (this.dayInternalInvalid || (!this.dayInput && !this.hideDay)) {
                this.dayFocused = true;
            } else if (this.yearInternalInvalid || !this.yearInput) {
                this.yearFocused = true;
            }
        },
        validation_invalid () {
            return this.dateInvalid ||
                   this.partiallyFilled ||
                   this.monthInternalInvalid ||
                   this.dayInternalInvalid ||
                   this.yearInternalInvalid ||
                   this.dateInvalidTo ||
                   this.dateInvalidFrom;
        },
        validation_isEmpty () {
            return !this.dayInput && !this.yearInput && !this.monthInput;
        },
    },
};
</script>
